import { useMemo, useState, type FormEvent } from 'react'
import { AlertCircle, ChevronDown, ChevronLeft, ChevronRight, Download, Loader2, RotateCcw, Search } from 'lucide-react'
import { auditCenterApi } from '@/api/auditCenterApi'
import { useAuditSearch, useFieldLibraryLabels } from '@/hooks/useAuditSearch'
import { AuditTrendsPanel } from '@/components/audit/AuditTrendsPanel'
import { useAuth } from '@/providers/AuthProvider'
import { useTenant } from '@/providers/TenantProvider'
import { usePermission, PERMISSIONS } from '@/lib/permissions'
import { cn } from '@/lib/utils'
import {
  SEARCH_PLACEHOLDER,
  advancedFieldsFor,
  AUDIT_CENTER_SOURCES,
  EMPTY_CRITERIA,
  EXPORT_COLUMNS,
  auditSource,
  canSearch,
  criteriaLines,
  criteriaToParams,
  exportFileName,
  userActivity,
  exportRow,
  hasAdvancedCriteria,
  onReadableTrail,
  PATIENT_EVENT_KINDS,
  readableTrails,
  type AuditCriteria,
} from '@/lib/auditCenter'
import type { AuditCenterRow, AuditCenterSource, PatientEventKind } from '@/types/audit'
import { AuditTrailReport, useAuditViewColumns } from '@/components/audit/AuditTrailReport'
import { AUDIT_VIEWS } from '@/lib/auditViews'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

/** Rows per page, as the other paged tables offer them. */
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100]
const DEFAULT_PAGE_SIZE = 50
/** The deepest the backend pages (its MAX_RESULT_WINDOW), and so the most one export holds. */
const EXPORT_LIMIT = 10_000
const EXPORT_PAGE = 1000

/**
 * The Audit Trail Center (z8nrz7dq6q, spec 2026-09-22; revised with the client
 * 2026-09-29): search ONE audit trail at a time, read it the way that trail's
 * own screen presents it, follow any record to its own trail, and export what
 * was found.
 *
 * Two homes, one component. The Center page owns its search in the URL
 * (`criteria` + `onCriteriaChange`). Inventory, Storage and Equipment embed it
 * with `lockedSources`, so the trail a person sees "from the point of use" is
 * the same search, the same columns and the same export as the central one —
 * just fixed to that page's trail.
 */
export function AuditTrailCenter({
  lockedSources,
  criteria: controlled,
  onCriteriaChange,
  scope,
  tableId = 'audit-center-v1',
}: {
  /** Fix the search to these trails and hide the source chips. */
  lockedSources?: AuditCenterSource[]
  /** The applied search, when the parent keeps it (the Center page, in its URL). */
  criteria?: AuditCriteria
  onCriteriaChange?: (next: AuditCriteria) => void
  /** Names the export file: audit-trail-<scope>-<day>.xlsx. */
  scope?: string
  tableId?: string
}) {
  const { can, canAny } = usePermission()
  const { email } = useAuth()
  const { selectedTenant } = useTenant()

  const [internal, setInternal] = useState<AuditCriteria>(EMPTY_CRITERIA)
  const applied = controlled ?? internal
  const setApplied = (next: AuditCriteria) => (onCriteriaChange ? onCriteriaChange(next) : setInternal(next))

  // The form edits a draft; Search applies it. A new applied search (from the
  // URL, a chip, Reset) replaces the draft — adjusted during render, not in an
  // effect, so the form never shows one frame of the old values.
  const [draft, setDraft] = useState<AuditCriteria>(applied)
  const [draftOf, setDraftOf] = useState<AuditCriteria>(applied)
  if (draftOf !== applied) {
    setDraftOf(applied)
    setDraft(applied)
  }

  const [advancedOpen, setAdvancedOpen] = useState(() => hasAdvancedCriteria(applied))
  const [skip, setSkip] = useState(0)
  const [take, setTake] = useState(DEFAULT_PAGE_SIZE)
  const [skipOf, setSkipOf] = useState<AuditCriteria>(applied)
  if (skipOf !== applied) {
    setSkipOf(applied)
    setSkip(0)
  }

  // Only the trails this user's role may read get a chip (audit.read, or that
  // trail's own audit.<trail>.read). Kept as a string so the memo below only
  // changes when the trails do, not on every render.
  const readableKey = readableTrails(canAny).join(',')
  const readable = useMemo(() => (readableKey ? (readableKey.split(',') as AuditCenterSource[]) : []), [readableKey])
  const effective = useMemo(
    () => (lockedSources ? { ...applied, sources: lockedSources } : onReadableTrail(applied, readable)),
    [applied, lockedSources, readable],
  )
  const area = auditSource(effective)
  const activity = userActivity(effective)

  // The table's order is part of the search: the server sorts everything
  // paging reaches, so a column sort is never just the page on screen. A new
  // order starts again from the first page.
  const auditView = AUDIT_VIEWS[area]
  const cols = useAuditViewColumns(auditView)
  const sortColumn = cols.sort ? auditView.columns.find((c) => c.id === cols.sort?.id) : undefined
  const sort = cols.sort && sortColumn?.sortKey ? `${sortColumn.sortKey}:${cols.sort.dir}` : undefined
  const [sortOf, setSortOf] = useState(sort)
  if (sortOf !== sort) {
    setSortOf(sort)
    setSkip(0)
  }
  const order =
    !cols.sort || !sortColumn
      ? 'newest first'
      : sortColumn.sortKey === 'when'
        ? cols.sort.dir === 'asc'
          ? 'oldest first'
          : 'newest first'
        : `sorted by ${sortColumn.header}, ${cols.sort.dir === 'asc' ? 'ascending' : 'descending'}`

  const params = useMemo(() => ({ ...criteriaToParams(effective), skip, take, sort }), [effective, skip, take, sort])
  // The Patient trail is one patient's history: nothing is searched until one
  // is named. The guard on `result` matters as much as the disabled query —
  // the hook keeps the previous search's rows on screen while a new one loads,
  // and those would be another trail's.
  const searchable = canSearch(effective)
  const search = useAuditSearch(params, { enabled: searchable })
  const result = searchable ? search.data : undefined

  // Results (the rows) or Trends (spec page 4) — both of the same search.
  const [view, setView] = useState<'results' | 'trends'>('results')
  const fieldLabels = useFieldLibraryLabels({ enabled: advancedOpen })
  const trendParams = useMemo(() => criteriaToParams(effective), [effective])

  const [exporting, setExporting] = useState(false)
  const [exportNote, setExportNote] = useState<string | null>(null)

  function submit(e: FormEvent) {
    e.preventDefault()
    setApplied(draft)
  }

  function selectSource(value: AuditCenterSource) {
    // Applies at once, keeping whatever text is typed but not yet searched.
    setApplied({ ...draft, sources: [value] })
  }

  function reset() {
    setApplied(EMPTY_CRITERIA)
    setExportNote(null)
  }

  async function exportExcel() {
    setExporting(true)
    setExportNote(null)
    try {
      // The file follows the table's order.
      const base = { ...criteriaToParams(effective), sort }
      const rows: AuditCenterRow[] = []
      let total = 0
      for (let at = 0; at < EXPORT_LIMIT; at += EXPORT_PAGE) {
        const page = await auditCenterApi.search({ ...base, skip: at, take: EXPORT_PAGE })
        total = page.total
        rows.push(...page.items)
        if (page.items.length < EXPORT_PAGE || rows.length >= total) break
      }
      const { default: ExcelJS } = await import('exceljs')
      const wb = new ExcelJS.Workbook()
      const sheet = wb.addWorksheet('Audit trail')
      sheet.addRow([...EXPORT_COLUMNS])
      sheet.getRow(1).font = { bold: true }
      for (const row of rows) sheet.addRow(exportRow(row))
      sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: EXPORT_COLUMNS.length } }
      sheet.views = [{ state: 'frozen', ySplit: 1 }]
      sheet.columns.forEach((col, i) => {
        col.width = [24, 12, 30, 28, 11, 22, 30, 30, 24, 50][i] ?? 18
      })
      const context = wb.addWorksheet('Search criteria')
      const now = new Date()
      for (const line of criteriaLines(effective, {
        tenant: selectedTenant?.name ?? '',
        exportedBy: email ?? '',
        exportedAt: now,
        total,
        exported: rows.length,
      })) {
        context.addRow(line)
      }
      context.getColumn(1).font = { bold: true }
      context.getColumn(1).width = 18
      context.getColumn(2).width = 60

      const buf = await wb.xlsx.writeBuffer()
      const url = URL.createObjectURL(
        new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }),
      )
      const a = document.createElement('a')
      a.href = url
      a.download = exportFileName(now, scope)
      a.click()
      URL.revokeObjectURL(url)
      if (total > rows.length) {
        setExportNote(
          `Exported the newest ${rows.length.toLocaleString()} of ${total.toLocaleString()} rows. Narrow the search to export the rest.`,
        )
      }
    } catch (e) {
      setExportNote(e instanceof Error ? `Export failed: ${e.message}` : 'Export failed.')
    } finally {
      setExporting(false)
    }
  }

  // From the page the server returned, not the page asked for: while the next
  // page loads the previous one stays on screen, and the count must describe
  // the rows actually shown.
  const shownFrom = result && result.total > 0 ? result.skip + 1 : 0
  const shownTo = result ? Math.min(result.skip + result.items.length, result.total) : 0

  return (
    <div className='space-y-4'>
      <form onSubmit={submit} className='space-y-4 rounded-lg border bg-card p-4'>
        {!lockedSources && (
          <div className='space-y-2'>
            <span id={`${tableId}-trail`} className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'>
              Audit trail
            </span>
            {/* One trail at a time (the client, 2026-09-29): each is read the
                way its own screen shows it, so trails are not mixed. */}
            <div className='flex flex-wrap gap-2' role='radiogroup' aria-labelledby={`${tableId}-trail`}>
              {AUDIT_CENTER_SOURCES.filter((s) => readable.includes(s.value)).map(({ value, label }) => {
                const on = area === value
                const n = on ? (activity ? result?.total : result?.counts[value]) : undefined
                return (
                  <button
                    key={value}
                    type='button'
                    role='radio'
                    aria-checked={on}
                    onClick={() => selectSource(value)}
                    className={cn(
                      'inline-flex min-w-[6.5rem] items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      on
                        ? 'bg-primary text-primary-foreground shadow-sm hover:bg-primary/90'
                        : 'bg-primary/10 text-primary hover:bg-primary/15',
                    )}
                  >
                    {label}
                    {n !== undefined && (
                      <span className={cn('tabular-nums', on ? 'text-primary-foreground/80' : 'text-primary/70')}>
                        {n.toLocaleString()}
                      </span>
                    )}
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {area === 'user' && (
          <div className='space-y-2 border-t pt-4'>
            <Label
              htmlFor={`${tableId}-person`}
              className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
            >
              User
            </Label>
            <div className='flex flex-wrap gap-2'>
              <Input
                id={`${tableId}-person`}
                className='min-w-[16rem] max-w-md flex-1'
                placeholder='Name or email'
                value={draft.changedBy}
                onChange={(e) => setDraft((d) => ({ ...d, changedBy: e.target.value }))}
              />
              <Button type='submit' variant='outline' className='min-w-[6.5rem]'>
                Show activity
              </Button>
            </div>
          </div>
        )}

        {area === 'patient' && (
          <div className='space-y-2 border-t pt-4'>
            <Label
              htmlFor={`${tableId}-patient`}
              className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
            >
              Patient
            </Label>
            <div className='flex flex-wrap gap-2'>
              <Input
                id={`${tableId}-patient`}
                className='min-w-[16rem] max-w-md flex-1'
                placeholder='MRN or name'
                value={draft.patient}
                onChange={(e) => setDraft((d) => ({ ...d, patient: e.target.value }))}
              />
              <select
                aria-label='Kind of event'
                value={draft.kind}
                // A kind narrows the history at once, as a trail chip does.
                onChange={(e) => {
                  const kind = e.target.value as PatientEventKind | ''
                  setDraft((d) => ({ ...d, kind }))
                  setApplied({ ...draft, kind })
                }}
                className='h-9 rounded-md border bg-background px-2 text-sm'
              >
                <option value=''>All kinds</option>
                {PATIENT_EVENT_KINDS.map((k) => (
                  <option key={k.value} value={k.value}>
                    {k.label}
                  </option>
                ))}
              </select>
              <Button type='submit' variant='outline' className='min-w-[6.5rem]'>
                Show history
              </Button>
            </div>
          </div>
        )}

        <div className={cn('space-y-2', !lockedSources && 'border-t pt-4')}>
          <Label
            htmlFor={`${tableId}-q`}
            className='text-[11px] font-semibold uppercase tracking-wide text-muted-foreground'
          >
            General search
          </Label>
          <div className='flex flex-wrap gap-2'>
            <div className='relative min-w-[16rem] flex-1'>
              <Search className='pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground' />
              <Input
                id={`${tableId}-q`}
                className='pl-9'
                placeholder={SEARCH_PLACEHOLDER[area]}
                value={draft.q}
                onChange={(e) => setDraft((d) => ({ ...d, q: e.target.value }))}
              />
            </div>
            <Button type='submit' className='min-w-[6.5rem]'>
              Search
            </Button>
          </div>
          <div className='flex items-center gap-4'>
            <button
              type='button'
              aria-expanded={advancedOpen}
              onClick={() => setAdvancedOpen((o) => !o)}
              className='inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline'
            >
              <ChevronDown className={cn('h-3.5 w-3.5 transition-transform', advancedOpen && 'rotate-180')} />
              Advanced search
            </button>
            <button
              type='button'
              onClick={reset}
              className='inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline'
            >
              <RotateCcw className='h-3 w-3' />
              Reset
            </button>
          </div>
        </div>

        {advancedOpen && (
          <div className='grid gap-3 border-t pt-4 sm:grid-cols-2 lg:grid-cols-4'>
            {advancedFieldsFor(area).map((f) => (
              <div key={f.key} className='space-y-1'>
                <Label htmlFor={`${tableId}-${f.key}`} className='text-xs'>
                  {f.label}
                </Label>
                <Input
                  id={`${tableId}-${f.key}`}
                  className='h-9'
                  placeholder={f.key === 'field' ? 'Pick a field-library field, or type' : f.placeholder}
                  value={draft[f.key]}
                  // "Any field from the field library" — suggested from the library,
                  // still free text for a field that is not in it.
                  list={f.key === 'field' ? `${tableId}-field-library` : undefined}
                  autoComplete='off'
                  onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                />
                {f.key === 'field' && (
                  <datalist id={`${tableId}-field-library`}>
                    {(fieldLabels.data ?? []).map((label) => (
                      <option key={label} value={label} />
                    ))}
                  </datalist>
                )}
              </div>
            ))}
            <div className='space-y-1'>
              <Label htmlFor={`${tableId}-from`} className='text-xs'>
                From
              </Label>
              <Input
                id={`${tableId}-from`}
                type='date'
                className='h-9'
                value={draft.fromDay}
                max={draft.toDay || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, fromDay: e.target.value }))}
              />
            </div>
            <div className='space-y-1'>
              <Label htmlFor={`${tableId}-to`} className='text-xs'>
                To
              </Label>
              <Input
                id={`${tableId}-to`}
                type='date'
                className='h-9'
                value={draft.toDay}
                min={draft.fromDay || undefined}
                onChange={(e) => setDraft((d) => ({ ...d, toDay: e.target.value }))}
              />
            </div>
          </div>
        )}
      </form>

      <div className='flex items-center gap-1 rounded-lg border bg-muted/40 p-0.5 w-fit' role='tablist' aria-label='Results or trends'>
        {(['results', 'trends'] as const).map((v) => (
          <button
            key={v}
            type='button'
            role='tab'
            aria-selected={view === v}
            onClick={() => setView(v)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              view === v ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {v === 'results' ? 'Results' : 'Trends'}
          </button>
        ))}
      </div>

      {!searchable ? (
        <div className='rounded-lg border bg-card px-4 py-10 text-center text-sm text-muted-foreground'>
          Enter a patient&apos;s MRN or name to see everything that happened for them.
        </div>
      ) : view === 'trends' ? (
        <AuditTrendsPanel params={trendParams} />
      ) : (
      <>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <p className='text-sm text-muted-foreground' aria-live='polite'>
          {search.isLoading
            ? 'Searching…'
            : result
              ? result.total === 0
                ? 'No results'
                : `${shownFrom.toLocaleString()}–${shownTo.toLocaleString()} of ${result.total.toLocaleString()} results, ${order}`
              : null}
          {search.isFetching && !search.isLoading && <Loader2 className='ml-2 inline h-3.5 w-3.5 animate-spin' />}
        </p>
        {can(PERMISSIONS.auditExport) && (
          <Button
            type='button'
            variant='outline'
            size='sm'
            disabled={exporting || !result || result.total === 0}
            onClick={exportExcel}
          >
            {exporting ? <Loader2 className='mr-2 h-4 w-4 animate-spin' /> : <Download className='mr-2 h-4 w-4' />}
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        )}
      </div>
      {exportNote && <p className='text-xs text-amber-700'>{exportNote}</p>}

      {search.isError ? (
        <div className='flex items-center gap-2 rounded-lg border px-4 py-6 text-sm text-destructive'>
          <AlertCircle className='h-4 w-4 shrink-0' />
          Could not search the audit trail. {search.error.message}
        </div>
      ) : (
        // Each trail in its own layout (the client, 2026-09-29).
        <AuditTrailReport
          view={auditView}
          columns={cols}
          rows={result?.items ?? []}
          isLoading={search.isLoading}
        />
      )}

      {/* The same pager as the other paged tables. Paging stops at the newest
          10,000 matches — the server's window — and says so. */}
      {result && result.total > 0 && (
        <div className='flex flex-wrap items-center justify-between gap-2 rounded-lg border bg-card px-4 py-2 text-xs text-muted-foreground'>
          <span>
            {shownFrom.toLocaleString()}–{shownTo.toLocaleString()} of {result.total.toLocaleString()} changes
            {result.total > EXPORT_LIMIT && ` · paging reaches the newest ${EXPORT_LIMIT.toLocaleString()}`}
          </span>
          <div className='flex items-center gap-2'>
            <select
              aria-label='Rows per page'
              value={take}
              onChange={(e) => {
                setTake(Number(e.target.value))
                setSkip(0)
              }}
              className='rounded border bg-background px-2 py-0.5 text-xs'
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
            <div className='flex items-center gap-1'>
              <button
                type='button'
                className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                disabled={skip === 0 || search.isFetching}
                onClick={() => setSkip((s) => Math.max(0, s - take))}
              >
                <ChevronLeft className='h-3.5 w-3.5' />
                Previous
              </button>
              <button
                type='button'
                className='inline-flex items-center gap-1 rounded-md px-2 py-1 hover:bg-accent disabled:opacity-40'
                disabled={skip + take >= Math.min(result.total, EXPORT_LIMIT) || search.isFetching}
                onClick={() => setSkip((s) => s + take)}
              >
                Next
                <ChevronRight className='h-3.5 w-3.5' />
              </button>
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  )
}
